import { useState } from 'react';

import { PageHeader } from '@@/PageHeader';
import { useTableStateWithoutStorage } from '@@/datatables/useTableState';
import { BEOverlay } from '@@/BEFeatureIndicator/BEOverlay';

import { FeatureId } from '../../feature-flags/enums';

import { ActivityLogsTable } from './ActivityLogsTable';
import { useActivityLogs, getSortType } from './useActivityLogs';
import { useExportMutation } from './useExportMutation';
import { FilterBar } from './FilterBar';
import { FilterState } from './types';
import { useAccumulatedValues } from './useAccumulatedValues';

export function ActivityLogsView() {
  const exportMutation = useExportMutation();

  const [filters, setFilters] = useState<FilterState>({
    range: { start: null, end: null },
    usernames: [],
    contexts: [],
  });

  const [page, setPage] = useState(0);
  const tableState = useTableStateWithoutStorage('Timestamp', true);

  const offset = page * tableState.pageSize;
  const { range } = filters;
  const query = {
    offset,
    limit: tableState.pageSize,
    sortBy: getSortType(tableState.sortBy?.id),
    sortDesc: tableState.sortBy?.desc,
    keyword: tableState.search,
    ...(range
      ? {
          after: seconds(range?.start?.valueOf()),
          before: seconds(range?.end?.valueOf()),
        }
      : undefined),
    username: filters.usernames,
    context: filters.contexts,
  };

  const logsQuery = useActivityLogs(query);

  const environments = useAccumulatedValues(
    logsQuery.data?.logs,
    (log) => log.context
  );
  const users = useAccumulatedValues(
    logsQuery.data?.logs,
    (log) => log.username
  );

  return (
    <>
      <PageHeader
        title="User activity logs"
        breadcrumbs="User activity logs"
        reload
      />

      <div className="mx-4">
        <BEOverlay featureId={FeatureId.ACTIVITY_AUDIT}>
          <FilterBar
            value={filters}
            setState={setFilters}
            onExport={handleExport}
            environments={environments}
            users={users}
          />

          <div className="-mx-[15px] mt-4">
            <ActivityLogsTable
              sort={tableState.sortBy}
              onChangeSort={(value) =>
                tableState.setSortBy(value?.id, value?.desc || false)
              }
              limit={tableState.pageSize}
              onChangeLimit={tableState.setPageSize}
              keyword={tableState.search}
              onChangeKeyword={tableState.setSearch}
              currentPage={page}
              onChangePage={setPage}
              totalItems={logsQuery.data?.totalCount || 0}
              dataset={logsQuery.data?.logs}
              filterState={filters}
              onChangeFilterState={setFilters}
              filterOptions={{ username: users, context: environments }}
            />
          </div>
        </BEOverlay>
      </div>
    </>
  );

  function handleExport() {
    exportMutation.mutate(query);
  }
}

function seconds(ms?: number) {
  if (!ms) {
    return undefined;
  }

  return Math.floor(ms / 1000);
}
